/**
 * Runs NpcScheduleStep lists for every NPC in a scene on the fixed step. Each NPC gets its own
 * SeededRng fork (`rng.fork(npcId)`) for wait jitter, so the same seed + same add() order yields
 * identical positions. On a weather change the NPC swaps to `weatherBehaviour[state]` when defined
 * (falls back to its base schedule), restarting from step 0.
 *
 * Weather switch ownership: by default the runner listens to bus `weather:change` itself (switching at
 * the first emission of a transition, t = 0). A scene that wants to be the single owner of the switch
 * (WorldScene calls `setWeather()` on transition start) passes `{ bus: false }` and the runner never
 * touches the bus. Phaser-free at runtime (scene events by name) so it is unit-tested in Node.
 */
import type Phaser from 'phaser';
import { bus, Events, type EventPayloads } from '@/core/events';
import type { SeededRng } from '@/core/rng';
import type { NpcScheduleStep } from '@/core/types/contracts';
import type { WeatherState } from '@/core/types';
import { ActorSystem, StepOrder, type Steppable } from './ActorSystem';
import { ScheduleProgram, type ScheduleActorLike, type ScheduleOptions } from './schedule';
import { SceneEvents } from './sceneEvents';

type WeatherBehaviour = Partial<Record<WeatherState, NpcScheduleStep[]>>;
interface Entry { actor: ScheduleActorLike; program: ScheduleProgram; schedule: NpcScheduleStep[]; weather?: WeatherBehaviour; active: NpcScheduleStep[] }

export interface NpcScheduleRunnerOptions extends ScheduleOptions {
  initialWeather?: WeatherState;
  /**
   * Subscribe to bus `weather:change` (default true). Pass `false` when the owning scene calls
   * `setWeather()` itself so there is exactly one owner of the behaviour switch.
   */
  bus?: boolean;
}

export class NpcScheduleRunner implements Steppable {
  private readonly npcs = new Map<string, Entry>();
  private paused = false;
  private weather: WeatherState;
  private destroyed = false;
  private readonly system: ActorSystem;
  private readonly listensToBus: boolean;
  private readonly onWeather = (p: EventPayloads[typeof Events.WEATHER_CHANGE]) => this.setWeather(p.state);
  private readonly onShutdown = () => this.destroy();

  constructor(readonly scene: Phaser.Scene, private readonly rng: SeededRng, private readonly opts: NpcScheduleRunnerOptions = {}) {
    this.weather = opts.initialWeather ?? 'sultry';
    this.listensToBus = opts.bus !== false;
    this.system = ActorSystem.get(scene);
    this.system.add(this, StepOrder.SCHEDULE);
    if (this.listensToBus) bus.onTyped(Events.WEATHER_CHANGE, this.onWeather);
    scene.events.once(SceneEvents.SHUTDOWN, this.onShutdown);
    scene.events.once(SceneEvents.DESTROY, this.onShutdown);
  }

  add(id: string, actor: ScheduleActorLike, schedule: NpcScheduleStep[] = [], weather?: WeatherBehaviour): ScheduleProgram {
    this.remove(id);
    const active = weather?.[this.weather] ?? schedule;
    const program = new ScheduleProgram(active, this.rng.fork(id), { jitter: this.opts.jitter, walkSpeed: this.opts.walkSpeed });
    if (this.paused) program.setPaused(true, actor);
    const entry: Entry = { actor, program, schedule, active };
    if (weather) entry.weather = weather;
    this.npcs.set(id, entry);
    return program;
  }

  remove(id: string): void {
    const e = this.npcs.get(id);
    if (!e) return;
    if (e.program.currentPhase === 'walking') e.actor.stop();
    this.npcs.delete(id);
  }

  has(id: string): boolean { return this.npcs.has(id); }
  get(id: string): ScheduleProgram | null { return this.npcs.get(id)?.program ?? null; }
  get size(): number { return this.npcs.size; }
  get currentWeather(): WeatherState { return this.weather; }
  /** True when this runner switches behaviours from bus `weather:change` itself (`bus` option). */
  get followsBus(): boolean { return this.listensToBus; }

  /** Freeze all schedules (dialogue / cinematics). */
  setPaused(on: boolean): void {
    this.paused = on;
    for (const e of this.npcs.values()) e.program.setPaused(on, e.actor);
  }
  isPaused(): boolean { return this.paused; }

  /** Switch every NPC with a `weatherBehaviour[state]` to it (others keep their base schedule); no-op for the current state. */
  setWeather(state: WeatherState): void {
    if (state === this.weather) return;
    this.weather = state;
    for (const e of this.npcs.values()) {
      const list = e.weather?.[state] ?? e.schedule;
      if (list === e.active) continue;
      e.active = list;
      e.program.setSteps(list, e.actor);
    }
  }

  step(dtMs: number): void {
    if (this.destroyed || this.paused) return;
    for (const e of this.npcs.values()) e.program.step(e.actor, dtMs);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.listensToBus) bus.offTyped(Events.WEATHER_CHANGE, this.onWeather);
    this.system.remove(this);
    this.npcs.clear();
    this.scene.events.off(SceneEvents.SHUTDOWN, this.onShutdown);
    this.scene.events.off(SceneEvents.DESTROY, this.onShutdown);
  }
}
